import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Loader2, RefreshCw, ScrollText, Wrench, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLS = [["domain", "Domain"], ["dns", "DNS"], ["nameserver", "NS"], ["worker", "Worker"], ["ssl", "SSL"]];
const DOT = { ok: "bg-emerald-500", pending: "bg-amber-400 animate-pulse", error: "bg-rose-500", skipped: "bg-slate-300" };
const LABEL = { ok: "OK", pending: "Ausstehend", error: "Fehler", skipped: "Übersprungen" };

function Dot({ state, testid }) {
  return <span title={LABEL[state]} data-testid={testid} data-state={state} className={`inline-block w-3 h-3 rounded-full ${DOT[state]}`} />;
}

export function AdminDomains({ adminToken, tick }) {
  const [items, setItems] = useState(null);
  const [auto, setAuto] = useState(true);
  const [busy, setBusy] = useState({});
  const [detail, setDetail] = useState(null);
  const headers = { Authorization: `Bearer ${adminToken}` };

  const load = useCallback(() => {
    axios.get(`${API}/admin/provisioning`, { headers: { Authorization: `Bearer ${adminToken}` } }).then(r => setItems(r.data.items)).catch(() => toast.error("Provisioning konnte nicht geladen werden"));
  }, [adminToken]);
  useEffect(() => { load(); }, [load, tick]);
  useEffect(() => {
    if (!auto) return undefined;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [auto, load]);

  const run = async (id, key, fn) => {
    setBusy(b => ({ ...b, [id + key]: true }));
    try { await fn(); } finally { setBusy(b => ({ ...b, [id + key]: false })); }
  };
  const recheck = (it) => run(it.id, "r", async () => {
    try {
      const r = await axios.post(`${API}/admin/provisioning/${it.id}/recheck`, {}, { headers });
      toast[r.data.reachable ? "success" : "warning"](r.data.reachable ? `${it.domain} ist erreichbar` : `${it.domain} nicht erreichbar (HTTP ${r.data.status_code || "–"}, IP ${r.data.ip || "keine"})`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Prüfung fehlgeschlagen"); }
  });
  const reprovision = (it) => {
    if (!window.confirm(`Einrichtung für ${it.domain} erneut starten (DNS, SSL, Worker)? Es wird keine neue Domain gekauft.`)) return;
    run(it.id, "p", async () => {
      try {
        const r = await axios.post(`${API}/admin/approve-domain/${it.id}`, { register_domain: false }, { headers });
        toast[r.data.success ? "success" : "error"](r.data.success ? "Einrichtung abgeschlossen" : "Einrichtung fehlgeschlagen – siehe Log");
        load();
      } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    });
  };
  const openLog = async (it) => {
    try { const r = await axios.get(`${API}/admin/provisioning/${it.id}`, { headers }); setDetail({ it, ...r.data }); }
    catch { toast.error("Log konnte nicht geladen werden"); }
  };

  if (!items) return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>;
  return (
    <div data-testid="admin-domains">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">{items.length} Seiten · <span className="inline-flex items-center gap-1"><Dot state="ok" /> OK</span> <span className="inline-flex items-center gap-1 ml-2"><Dot state="pending" /> Ausstehend</span> <span className="inline-flex items-center gap-1 ml-2"><Dot state="error" /> Fehler</span></p>
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer"><input type="checkbox" checked={auto} onChange={e => setAuto(e.target.checked)} data-testid="admin-domains-autorefresh" /> Auto-Refresh (15 s)</label>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-slate-500 border-b border-slate-200"><tr>
            <th className="px-4 py-3">Seite</th>{COLS.map(([k, l]) => <th key={k} className="px-2 py-3 text-center">{l}</th>)}<th className="px-2 py-3 text-center">HTTPS</th><th className="px-4 py-3 text-right">Aktionen</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400" data-testid="admin-domains-empty">Keine Domains vorhanden</td></tr>}
            {items.map(it => (
              <tr key={it.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/70" data-testid={`admin-domain-row-${it.id}`}>
                <td className="px-4 py-3">
                  <p className="font-semibold text-slate-900">{it.company_name || "–"}</p>
                  <p className="text-xs text-slate-500 flex items-center gap-1">{it.domain || "keine Domain"}{it.domain && <a href={`https://${it.domain}`} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-3 h-3" /></a>}</p>
                  {it.error && <p className="text-xs text-rose-600 mt-0.5 max-w-xs truncate" title={it.error}>{it.error}</p>}
                </td>
                {COLS.map(([k]) => <td key={k} className="px-2 py-3 text-center"><Dot state={it.steps[k]} testid={`admin-domain-status-${k}-${it.id}`} /></td>)}
                <td className="px-2 py-3 text-center"><Dot state={it.reachable ? "ok" : it.steps.live === "ok" ? "pending" : it.steps.live === "error" ? "error" : "pending"} testid={`admin-domain-status-https-${it.id}`} /></td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1.5">
                    {it.domain_type !== "subdomain" && <Button size="sm" variant="outline" disabled={busy[it.id + "r"]} onClick={() => recheck(it)} data-testid={`admin-btn-recheck-${it.id}`}>{busy[it.id + "r"] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}<span className="ml-1 hidden xl:inline">Prüfen</span></Button>}
                    <Button size="sm" variant="outline" onClick={() => openLog(it)} data-testid={`admin-btn-view-log-${it.id}`}><ScrollText className="w-3.5 h-3.5" /><span className="ml-1 hidden xl:inline">Log</span></Button>
                    {it.domain_type !== "subdomain" && <Button size="sm" variant="outline" disabled={busy[it.id + "p"]} onClick={() => reprovision(it)} data-testid={`admin-btn-reprovision-${it.id}`}>{busy[it.id + "p"] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wrench className="w-3.5 h-3.5" />}<span className="ml-1 hidden xl:inline">Neu einrichten</span></Button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Sheet open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="admin-provisioning-drawer">
          <SheetHeader><SheetTitle>{detail?.it.domain} – Provisioning</SheetTitle></SheetHeader>
          {detail && (
            <div className="mt-4 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(detail.provisioning).map(([k, v]) => (
                  <div key={k} className="border border-slate-200 rounded-lg p-2"><p className="text-xs text-slate-500">{k}</p><p className="font-medium">{v.status}</p>{v.error && <p className="text-xs text-rose-600 break-words">{v.error}</p>}</div>
                ))}
              </div>
              <div>
                <p className="font-semibold mb-1">Log</p>
                <pre className="font-mono text-xs bg-slate-900 text-slate-100 rounded-lg p-3 overflow-x-auto max-h-96 whitespace-pre-wrap" data-testid="admin-provisioning-log">{detail.log.length ? JSON.stringify(detail.log, null, 2) : "Kein Log vorhanden"}</pre>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
